import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { Email } from '@/types'

import {
  emailGetAll,
  emailSave,
  emailUpdate,
  emailDelete,
} from "@/store/email";

import { userUpdateProfile } from '@/store/auth'

import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppButton from "@/components/AppButton";
import AppText from "@/components/AppText";
import AppCard from "@/components/AppCard";
import AppIcon from "@/components/AppIcon";

interface FormState {
  name: string;
}

const FORM_WIDTH = 620;

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();

  const { me } = useAppSelector((state) => state.auth);
  const { emails, fetchAllStatus, status } = useAppSelector((state) => state.email);

  const [form, setForm] = useState<FormState>({ name: "" });
  const [alertEmails, setAlertEmails] = useState<Email[]>([]);

  useEffect(() => {
    if (me.id) {
      setForm({ name: me.name });
      dispatch(emailGetAll());
    }
  }, [me, dispatch]);

  useEffect(() => {
    if (fetchAllStatus === "success" && !emails.length) {
      dispatch(emailSave({ email: me.email }));
      dispatch(emailGetAll());
    }
  }, [fetchAllStatus, emails, dispatch, me.email]);

  useEffect(() => {
    if (emails.length) {
      setAlertEmails(emails);
    }
  }, [emails]);

  const handleChange = (field: keyof FormState) => (value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddField = () => {
    if (alertEmails.length < 5) {
      setAlertEmails([...alertEmails, { email: "" }]);
    }
  };

  const handleRemoveField = async (id?: number, index?: number) => {
    const existingEmail = alertEmails.find((item) => item.id === id);
    if (existingEmail?.id) {
      await dispatch(emailDelete(existingEmail.id));
      await dispatch(emailGetAll());
      return;
    }

    setAlertEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveInfo = async () => {
    for (const email of alertEmails) {
      if (email.id) {
        const prevEmail = emails.find((item) => item.id === email.id);

        if (prevEmail && prevEmail.email !== email.email) {
          await dispatch(
            emailUpdate({ id: email.id, data: { email: email.email } })
          );
        }
      } else {
        await dispatch(emailSave({ data: { email: email.email } }));
      }
    }

    if (me.name !== form.name) {
      await dispatch(userUpdateProfile({ data: form }));
    }

    dispatch(emailGetAll());
  };

  return (
    <AppBox className="flex">
      <AppBox column gap={6} fullHeight>
        <AppCard className="gap-5">
          {/* Profile Information */}
          <AppBox column justify="between"  width={FORM_WIDTH / 2}>
            <AppText variant="h6">
              Profile Information
            </AppText>
            <AppInput
              type="text"
              label="Your Name"
              onChange={(e) => handleChange("name")(e.target.value)}
              value={form.name}
              onPressEnter={handleSaveInfo}
            />
          </AppBox>

          {/* Emails for Alerts */}
          <AppBox className="flex flex-col justify-between">
            <AppText variant="h6" className="mb-2 flex gap-2 items-center">
              Emails for Alerts
            </AppText>
            <AppBox className="flex flex-col gap-2">
              {alertEmails.map((item, index) => (
                <AppBox key={index} className="flex">
                  <AppInput
                    type="text"
                    label={`Alert Email #${index + 1}`}
                    onChange={(e) => {
                      const updatedFields = [...alertEmails];
                      updatedFields[index] = {
                        ...updatedFields[index],
                        email: e.target.value,
                      };
                      setAlertEmails(updatedFields);
                    }}
                    value={item.email}
                    onPressEnter={handleSaveInfo}
                    className="mr-4"
                    style={{ width: FORM_WIDTH / 2 }}
                  />
                  {alertEmails.length > 1 && index > 0 && (
                    <AppIcon name="trash" className="text-error hover:text-error-hover" onClick={() => handleRemoveField(item.id, index)} />
                  )}
                </AppBox>
              ))}
              {alertEmails.length < 5 && (
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={handleAddField}
                  className="w-36"
                >
                  Add Email
                  <AppIcon name="add" />
                </AppButton>
              )}
              {alertEmails.length === 5 && (
                <AppButton
                  variant="outlined"
                  size="small"
                  disabled
                  className="w-60"
                >
                  Maximum limit exceeded
                  <AppIcon name="ban" />
                </AppButton>
              )}
            </AppBox>
          </AppBox>
        </AppCard>

        {/* Save Button */}
        <AppBox className="w-28">
          <AppButton
            onClick={handleSaveInfo}
            isLoading={status === "loading"}
          >
            Save
          </AppButton>
        </AppBox>
      </AppBox>
    </AppBox>
  );
};

export default Profile;